import React, { useEffect } from 'react';
import {
  makeStyles,
  Theme,
  createStyles,
  Button,
  Grid,
  CircularProgress,
  Hidden,
  ButtonGroup,
  Tooltip,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useReport } from '../../context/report';
import { green } from '@material-ui/core/colors';
import { useLocation } from 'react-router-dom';
import AutoSelect from './AutoSelect';
import { useInstitutional } from '../../context/institutional';
import SegmentSelector from './SegmentSelector';
import GroupSelector from './selectors/GroupSelector';
import DivisionSelector from './selectors/DivisionSelector';
import CountrySelector from './selectors/CountrySelector';
import FrequencySelector from './selectors/FrequencySelector';
import OperativeFilters from './dialogs/opstructure-filters';
import { useUser } from '../../context/user';
import { CloudDownload } from '@material-ui/icons';
import CompanyFilters from './dialogs/company-filters';
import { GROUPBY_DEFAULT, GROUPBY_REPORT, GROUPBY_OPSTRUCTURE } from '../../utils/constants';
import ServiceSelector from './selectors/ServiceSelector';
import { isAfter, isBefore, isSameDay, subDays, subMonths } from 'date-fns';
import { ReportGrouping } from '../../types';
import DisableToDateCheckbox from './selectors/DisableToDateCheckbox';
import DateRangePicker from './DateRangePicker';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      margin: theme.spacing(1),
      minWidth: 90,
    },
    selectEmpty: {
      marginTop: theme.spacing(2),
    },
    root: {
      marginBottom: theme.spacing(0),
    },
    grow: {
      flexGrow: 1,
    },
    buttonProgress: {
      color: green[500],
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -12,
      marginLeft: -12,
    },
    loadingWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
  })
);

interface Toolbar {
  getData: () => Promise<void>;
  requestExport?: () => Promise<void>;
  handleAdd?: () => void;
  dataIsLoading: boolean;
}

const Toolbar = ({ getData, requestExport, handleAdd, dataIsLoading }: Toolbar) => {
  const { filters, updateFilters, updateFrequency } = useReport();
  const { loading: loadingInstitutionalData, filteredStores } = useInstitutional();
  const classes = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const { division, stores, groupBy, disableToDate } = filters;
  const { permissions } = useUser();
  const [dateError, setDateError] = React.useState(false);

  const canFilterOperativeStructure = permissions.includes('FILTER_OPSTRUCTURE');
  const canExportReport =
    location.pathname.includes('report') && permissions.includes('EXPORT_REPORT');
  const inRanking = location.pathname.includes('rankings');
  const inCharts = location.pathname.includes('charts');
  const inExclusions = location.pathname.includes('exclusions');
  const inFranchisees = location.pathname.includes('franchisees');
  const inMfySide = location.pathname.includes('side');
  const loading = loadingInstitutionalData || dataIsLoading;
  const missingGrouping = groupBy.length === 0;
  const missingDivision = division.length === 0;
  const missingCountry = filters.countries.length === 0;
  const missingYear = filters.year === '' || filters.year === undefined;
  const missingStore = stores.length === 0;
  const disableFetch =
    ((missingDivision || missingGrouping) && !inRanking) ||
    ((inRanking || inCharts || inExclusions) && missingCountry) ||
    missingYear ||
    (inFranchisees && (missingDivision || missingCountry)) ||
    (inMfySide && (missingStore || missingCountry)) ||
    dateError ||
    loading;

  const inSameDay = isSameDay(filters.fromDate, filters.toDate);
  const showSegments =
    !(inExclusions && (!inSameDay || disableToDate)) && !inFranchisees && !inMfySide;
  const showGroupBy = !inRanking && !inCharts && !inExclusions && !inFranchisees && !inMfySide;

  const showOperativeFilters =
    canFilterOperativeStructure && !inExclusions && !inFranchisees && !inMfySide;

  const getOptions = () => {
    if (canFilterOperativeStructure)
      return GROUPBY_DEFAULT.concat(GROUPBY_REPORT).concat(GROUPBY_OPSTRUCTURE);
    return GROUPBY_DEFAULT.concat(GROUPBY_REPORT);
  };

  useEffect(() => {
    const options = getOptions();
    const gbe: ReportGrouping[] = ['country', 'store', 'businessDate', 'time'];
    const yesterday = subDays(new Date(), 1);
    const lastMonth = subMonths(yesterday, 1);
    const groupBy = inExclusions ? gbe : filters.groupBy.filter(e => options.includes(e));
    if (inExclusions) {
      updateFrequency('beginTime');
      if (
        isAfter(filters.fromDate, yesterday) ||
        isBefore(filters.fromDate, lastMonth) ||
        isAfter(filters.toDate, yesterday)
      ) {
        updateFilters({
          ...filters,
          division: filters.division.length > 1 ? [] : filters.division,
          countries: filters.countries.length > 1 ? [] : filters.countries,
          stores: filters.stores.length > 1 ? [] : filters.stores,
          fromDate: yesterday,
          toDate: yesterday,
          groupBy,
        });
      } else {
        updateFilters({
          ...filters,
          division: filters.division.length > 1 ? [] : filters.division,
          countries: filters.countries.length > 1 ? [] : filters.countries,
          stores: filters.stores.length > 1 ? [] : filters.stores,
          groupBy,
        });
      }
    } else if (inFranchisees) {
      updateFilters({
        ...filters,
        division: filters.division.length > 1 ? [] : filters.division,
        countries: filters.countries.length > 1 ? [] : filters.countries,
        stores: [],
      });
    } else if (filters.division.length > 1) {
      updateFilters({ ...filters, division: [], groupBy, disableToDate: false });
    } else if (inRanking && filters.stores.length > 0 && filteredStores.length < 2) {
      updateFilters({ ...filters, stores: [], groupBy, disableToDate: false });
    } else if (filters.stores.length === 0 && filteredStores.length < 2) {
      updateFilters({ ...filters, stores: [...filteredStores], groupBy, disableToDate: false });
    } else updateFilters({ ...filters, disableToDate: false });
    // eslint-disable-next-line
  }, []);

  const handleApplyFilters = async () => {
    await getData();
  };

  return (
    <Hidden implementation="css" mdDown>
      <Grid container item xs={12} className={classes.root} direction="column">
        {showGroupBy && (
          <Grid container item xs={12} direction="row">
            <GroupSelector />
            {groupBy.includes('time') && <FrequencySelector />}
          </Grid>
        )}
        {inExclusions && (
          <Grid container item xs={12} direction="row">
            <ServiceSelector />
            <DisableToDateCheckbox />
            <div className={classes.grow} />
            <div className={classes.loadingWrapper}>
              <ButtonGroup disabled={loading}>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleApplyFilters}
                  disabled={disableFetch}
                >
                  {t('reports.labels.search')}
                </Button>
                <Button color="primary" variant="contained" onClick={handleAdd}>
                  {`+ ${t('reports.labels.addExclusion')}`}
                </Button>
              </ButtonGroup>
              {loading && <CircularProgress size={22} className={classes.buttonProgress} />}
            </div>
          </Grid>
        )}

        <Grid container item xs={12} direction="row">
          <DivisionSelector />
          <CountrySelector />
          {!inFranchisees && <AutoSelect singleValue={inExclusions || inMfySide} />}
          {!inFranchisees && <DateRangePicker error={dateError} setError={setDateError} />}
          {showSegments && <SegmentSelector />}
          {showOperativeFilters && <OperativeFilters />}
          {showOperativeFilters && <CompanyFilters />}
          <div className={classes.grow} />
          {!inExclusions && (
            <div className={classes.loadingWrapper}>
              <ButtonGroup>
                <Button
                  id="toolbar-apply"
                  color="primary"
                  variant="contained"
                  disabled={disableFetch}
                  onClick={handleApplyFilters}
                >
                  {inFranchisees ? t('reports.labels.search') : t('reports.labels.apply')}
                </Button>
                {inFranchisees && (
                  <Button color="primary" variant="contained" onClick={handleAdd}>
                    {`+ ${t('reports.labels.addExclusion')}`}
                  </Button>
                )}
                {canExportReport && (
                  <Tooltip title={t('reports.labels.saveInOneDrive').toString()}>
                    <Button
                      id="toolbar-onedrive"
                      color="primary"
                      variant="contained"
                      disabled={disableFetch}
                      onClick={requestExport}
                    >
                      <CloudDownload />
                    </Button>
                  </Tooltip>
                )}
              </ButtonGroup>
              {loading && <CircularProgress size={22} className={classes.buttonProgress} />}
            </div>
          )}
        </Grid>
      </Grid>
    </Hidden>
  );
};

export default Toolbar;
